import { Component, Show, createMemo } from 'solid-js';

interface StatusBarProps {
  status: string;
  modelName?: string;
  isLoading?: boolean;
  isStreaming?: boolean;
  connectionStatus?: 'connected' | 'disconnected' | 'connecting';
  loadingProgress?: number;
  tokensEstimate?: number;
  latencyMs?: number;
  cps?: number; // characters per second of the last/ongoing response
}

export const StatusBar: Component<StatusBarProps> = (props) => {
  const statusInfo = createMemo(() => {
    const status = props.status;
    let type: 'info' | 'success' | 'warning' | 'error' = 'info';
    let hasProgress = false;
    let progressValue: number | undefined;

    if (status.includes('✅') || status.includes('loaded')) {
      type = 'success';
    } else if (status.includes('❌') || status.includes('Error')) {
      type = 'error';
    } else if (status.includes('Loading') || status.includes('Downloading')) {
      type = 'info';
      hasProgress = true;
      
      // Use provided progress or extract from status
      if (props.loadingProgress !== undefined) {
        progressValue = props.loadingProgress;
      } else {
        const percentMatch = status.match(/(\d+(?:\.\d+)?)%/);
        if (percentMatch) {
          progressValue = parseFloat(percentMatch[1]);
        }
      }
    }

    return { type, hasProgress, progressValue };
  });

  const getStatusIcon = () => {
    const type = statusInfo().type;
    if (type === 'success') {
      return '✅';
    } else if (type === 'error') {
      return '❌';
    } else {
      return props.isLoading || props.isStreaming ? '⏳' : 'ℹ️';
    }
  };

  // Keep ARIA role static to satisfy lint rules while still reflecting status semantics

  return (
    <section 
      class={`status status--${statusInfo().type}`}
      role="status"
      aria-label="Application status"
    >
      <div class="status-content">
        <span class="status-icon" role="img" aria-hidden="true">
          {getStatusIcon()}
        </span>
        
        <span class="status-text">
          {props.status}
        </span>

        <Show when={props.modelName}>
          <span class="model-info">
            Model: <strong>{props.modelName}</strong>
          </span>
        </Show>

        <Show when={statusInfo().hasProgress}>
          <div class="progress-container">
            <progress 
              class="progress-bar"
              value={statusInfo().progressValue}
              max="100"
              aria-label={`Loading progress: ${statusInfo().progressValue || 0}%`}
            >
              {statusInfo().progressValue || 0}%
            </progress>
            <span class="progress-text" aria-hidden="true">
              {(() => {
                const value = statusInfo().progressValue;
                return value !== undefined ? `${value.toFixed(1)}%` : 'Loading...';
              })()}
            </span>
          </div>
        </Show>

        <Show when={props.isStreaming}>
          <div class="streaming-indicator">
            <span class="streaming-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
            <span class="visually-hidden">AI is generating response</span>
          </div>
        </Show>

        <Show when={typeof props.tokensEstimate === 'number'}>
          <span class="token-info" title="Approximate context tokens">
            Ctx ~ {Math.max(0, Math.round(props.tokensEstimate || 0))}
          </span>
        </Show>

        <Show when={(props.latencyMs ?? 0) > 0}>
          <span class="token-info" title="Response timing">
            ⏱ {(props.latencyMs! / 1000).toFixed(1)}s
          </span>
        </Show>

        <Show when={(props.cps ?? 0) > 0}>
          <span class="token-info" title="Output speed (chars/sec)">
            • {Math.max(0, Math.round(props.cps!))} cps
          </span>
        </Show>
      </div>

      <Show when={props.connectionStatus}>
        <div 
          class={`connection-status connection-status--${props.connectionStatus}`}
          role="img"
          aria-label={`Connection status: ${props.connectionStatus}`}
        >
          <span class="connection-dot"></span>
          <span class="visually-hidden">{props.connectionStatus}</span>
        </div>
      </Show>
    </section>
  );
};